import { zodResolver } from '@hookform/resolvers/zod';
import { useForm as defaultUseForm, type DefaultValues, type Resolver } from 'react-hook-form';
import type { FieldValues, UseFormParams, UseFormResponse } from './types';

const useForm = <T extends FieldValues = FieldValues>(
  options: UseFormParams<T>
): UseFormResponse<T> => {
  const {
    onSubmit,
    nativeSubmit,
    resetOnSubmit,
    resolver,
    omitResetState,
    defaultValues,
    mode,
    context,
    criteriaMode,
    shouldFocusError,
    shouldUnregister,
    reValidateMode = 'onChange'
  } = options;

  const customResolver = resolver ? zodResolver(resolver) : undefined;

  const formMethods = defaultUseForm<T>({
    defaultValues,
    mode,
    context,
    criteriaMode,
    shouldFocusError,
    shouldUnregister,
    reValidateMode,
    resolver: customResolver as Resolver<T>
  });

  const customSubmit: typeof options.nativeSubmit = async (data, e) => {
    e?.preventDefault();

    if (resetOnSubmit && !!formMethods.formState.errors) {
      formMethods.reset(defaultValues as DefaultValues<T>, omitResetState);
    }

    if (onSubmit) {
      await onSubmit(data, formMethods, e);
    } else if (nativeSubmit) {
      nativeSubmit(data, e);
    }
  };

  const onSubmitHandler = formMethods.handleSubmit(customSubmit);

  return { ...formMethods, onSubmit: onSubmitHandler };
};

export default useForm;
